import React from 'react';

import { NetworkTier } from 'Containers/ResultPage/CompareModal';
import { SERVICE_TYPES } from 'Containers/ResultPage/constants';
import { ViewPlanPageCard } from 'Containers/ViewPlanPage/ViewPlanPageCard';
import { useTextContext } from 'Contexts/textContext';
import { Numbers, Paragraph } from 'DesignLibrary/atoms';
import { IconBlockProps } from 'DesignLibrary/molecules/IconBlock';
import Plan from 'Models/plan';
import Text from 'Shared/Text';
import { Recommendation } from 'Types/entities';

import { copayAsRange, renderCosts } from '../_shared';

interface DoctorVisitsCardProps {
  client: string;
  recommendation: Recommendation;
  selectedTier: NetworkTier;
}

export const DoctorVisitsCard: React.FC<DoctorVisitsCardProps> = ({
  client,
  recommendation,
  selectedTier,
}) => {
  const { retrieveContentfulData } = useTextContext();

  const noOonCoverageText =
    retrieveContentfulData<string>('health_section.compare_plan_text.compareModal.noOonCoverage') ||
    '{noOonCoverageText}';

  const title = retrieveContentfulData<string>(
    'health_details_section.plan_details.doctor_visits_card_title',
  );
  const icon: IconBlockProps = {
    icon: 'FirstAidKit',
    color: 'blue',
  };

  const plan = new Plan(recommendation);
  const defaultTier: NetworkTier = plan.hasMostPreferredNetwork ? 'most_preferred' : 'in_network';

  const getTierOverride = (costType: string): React.ReactNode | undefined => {
    if (selectedTier === defaultTier) return undefined;

    if (selectedTier === 'out_of_network') {
      if (!plan.hasOonCoverage()) {
        return <Paragraph size="small">{noOonCoverageText}</Paragraph>;
      }
      const tiers =
        costType === SERVICE_TYPES.PRIMARY_CARE ? plan.primaryCareTiers : plan.specialistCareTiers;
      const oonData = tiers?.out_of_network;
      if (oonData) {
        const suffix = oonData.deductible_applies ? '*' : '';
        if (plan.isNoDeductiblePlan && oonData.copay_min !== null && oonData.copay_max !== null) {
          return copayAsRange(oonData.copay_min, oonData.copay_max);
        }
        if (oonData.coinsurance !== null) return <Numbers>{`${oonData.coinsurance}%${suffix}`}</Numbers>;
        if (oonData.copay !== null) {
          return (
            <Numbers>
              {`$${oonData.copay}${suffix} `}
              <Text field="health_section.compare_plan_text.compareModal.perVisit" />
            </Numbers>
          );
        }
      }
      return <Paragraph size="small">{noOonCoverageText}</Paragraph>;
    }

    // comm-api bug: most_preferred and in_network keys are swapped in *_care_tiers for MP plans.
    // For non-MP plans, keys are correctly labeled — no swap needed.
    const hasMostPreferred = !!plan.hasMostPreferredNetwork;
    const swappedKey: NetworkTier = hasMostPreferred
      ? selectedTier === 'most_preferred'
        ? 'in_network'
        : selectedTier === 'in_network'
          ? 'most_preferred'
          : selectedTier
      : selectedTier;
    const tiers = costType === SERVICE_TYPES.PRIMARY_CARE ? plan.primaryCareTiers : plan.specialistCareTiers;
    const tierData = tiers?.[swappedKey];

    if (!tierData) return undefined;

    const suffix = tierData.deductible_applies ? '*' : '';
    if (tierData.coinsurance !== null) {
      return <Numbers>{`${tierData.coinsurance}%${suffix}`}</Numbers>;
    }
    if (tierData.copay !== null) {
      return (
        <Numbers>
          {`$${tierData.copay}${suffix} `}
          <Text field="health_section.compare_plan_text.compareModal.perVisit" />
        </Numbers>
      );
    }
    return undefined;
  };

  return (
    <ViewPlanPageCard title={title} icon={icon}>
      {Object.values(SERVICE_TYPES).map((costType, index) =>
        renderCosts(plan, client, costType, false, index, getTierOverride(costType)),
      )}
    </ViewPlanPageCard>
  );
};
